import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Tag, ShoppingCart, BadgeCheck, MessageCircle, Inbox, Flag } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import ImageUpload from "../components/ImageUpload";
import ChatDialog from "../components/ChatDialog";
import ReportDialog from "../components/ReportDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";

const CATEGORIES = ["All", "Toys", "Clothes", "Books", "Strollers"];
const CONDITIONS = ["All", "New", "Gently Used"];

export default function Marketplace() {
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [cat, setCat] = useState("All");
  const [cond, setCond] = useState("All");
  const [selected, setSelected] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [buying, setBuying] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatListingId, setChatListingId] = useState(null);
  const [reportTarget, setReportTarget] = useState(null);
  const [unread, setUnread] = useState(0);
  const loadUnread = () => api.get("/threads-unread/count").then(({ data }) => setUnread(data.unread)).catch(() => {});
  const [form, setForm] = useState({ title: "", description: "", category: "Toys", condition: "New", price: "", image: "" });

  const load = async () => {
    const { data } = await api.get("/listings");
    setListings(data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { loadUnread(); const id = setInterval(loadUnread, 15000); return () => clearInterval(id); }, []);

  const filtered = useMemo(() => listings.filter((l) =>
    (cat === "All" || l.category === cat) && (cond === "All" || l.condition === cond)
  ), [listings, cat, cond]);

  const createListing = async (e) => {
    e.preventDefault();
    try {
      await api.post("/listings", { ...form, price: parseFloat(form.price) });
      setCreateOpen(false);
      setForm({ title: "", description: "", category: "Toys", condition: "New", price: "", image: "" });
      toast.success("Listed for sale! 🏷️");
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const buy = async (listing) => {
    setBuying(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        listing_id: listing.listing_id,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBuying(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7] pb-16">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl sm:text-4xl font-display font-bold text-slate-900">Kids Marketplace 🛍️</h1>
            <p className="text-slate-500 mt-1">Buy & sell gently-loved toys, clothes and books.</p>
          </div>
          <div className="flex items-center gap-2">
            <Button data-testid="open-inbox-btn" variant="outline" onClick={() => { setChatListingId(null); setChatOpen(true); }}
              className="relative rounded-full font-bold px-4 h-11 border-2">
              <Inbox className="h-5 w-5 mr-1" /> Inbox
              {unread > 0 && (
                <span data-testid="inbox-unread-badge" className="absolute -top-1.5 -right-1.5 min-w-5 h-5 px-1 rounded-full bg-[#FF5A79] text-white text-xs font-bold flex items-center justify-center">{unread}</span>
              )}
            </Button>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button data-testid="open-create-listing-btn" className="rounded-full bg-[#FFC02D] text-slate-900 hover:bg-[#eab024] font-bold px-5 h-11 shadow-lg active:scale-95">
                <Plus className="h-5 w-5 mr-1" /> Post Item
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader><DialogTitle className="font-display text-2xl">Post an item for sale</DialogTitle></DialogHeader>
              <form onSubmit={createListing} className="space-y-4 mt-2" data-testid="create-listing-form">
                <div><Label>Title</Label>
                  <Input data-testid="listing-title-input" required className="mt-1 rounded-xl h-11" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Wooden train set" /></div>
                <div><Label>Description</Label>
                  <Textarea data-testid="listing-desc-input" required className="mt-1 rounded-xl" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Describe the item and condition" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Category</Label>
                    <select data-testid="listing-category-select" className="mt-1 w-full h-11 rounded-xl border border-input px-3" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                      {CATEGORIES.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}
                    </select></div>
                  <div><Label>Condition</Label>
                    <select data-testid="listing-condition-select" className="mt-1 w-full h-11 rounded-xl border border-input px-3" value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}>
                      {CONDITIONS.filter((c) => c !== "All").map((c) => <option key={c}>{c}</option>)}
                    </select></div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Price ($)</Label>
                    <Input data-testid="listing-price-input" type="number" step="0.01" min="0.5" required className="mt-1 rounded-xl h-11" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="19.99" /></div>
                  <div><Label>&nbsp;</Label><div className="text-xs text-slate-400 mt-3">Add a clear photo below 👇</div></div>
                </div>
                <div><Label>Photo</Label>
                  <div className="mt-1"><ImageUpload testid="listing-image-upload" value={form.image} onChange={(url) => setForm({ ...form, image: url })} /></div>
                </div>
                <Button type="submit" data-testid="submit-listing-btn" className="w-full h-11 rounded-xl bg-[#FF5A79] hover:bg-[#e04865] font-bold">List it for sale</Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row gap-8">
          {/* Sidebar filters */}
          <aside className="lg:w-56 shrink-0 space-y-6">
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-3">Category</div>
              <div className="flex lg:flex-col flex-wrap gap-2">
                {CATEGORIES.map((c) => (
                  <button key={c} data-testid={`cat-filter-${c}`} onClick={() => setCat(c)}
                    className={`text-left rounded-xl px-4 py-2 text-sm font-semibold transition ${cat === c ? "bg-[#FF5A79] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"}`}>{c}</button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider font-bold text-slate-400 mb-3">Condition</div>
              <div className="flex lg:flex-col flex-wrap gap-2">
                {CONDITIONS.map((c) => (
                  <button key={c} data-testid={`cond-filter-${c}`} onClick={() => setCond(c)}
                    className={`text-left rounded-xl px-4 py-2 text-sm font-semibold transition ${cond === c ? "bg-[#2AB7CA] text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"}`}>{c}</button>
                ))}
              </div>
            </div>
          </aside>

          {/* Grid */}
          <div className="flex-1">
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-6" data-testid="listings-grid">
              {filtered.map((l, i) => (
                <motion.div key={l.listing_id} data-testid={`listing-card-${l.listing_id}`}
                  initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }}
                  whileHover={{ y: -4 }}
                  className="bg-white rounded-3xl overflow-hidden border border-amber-100/60 shadow-lg hover:shadow-xl transition-all cursor-pointer"
                  onClick={() => setSelected(l)}>
                  <div className="relative aspect-[4/3]">
                    <img src={l.image} alt={l.title} className="h-full w-full object-cover" />
                    <span className="absolute top-3 left-3 rounded-full bg-white/90 backdrop-blur px-3 py-1 text-sm font-extrabold text-[#FF5A79]">${l.price.toFixed(2)}</span>
                    {l.sold && <span className="absolute top-3 right-3 rounded-full bg-slate-900 text-white px-3 py-1 text-xs font-bold">SOLD</span>}
                  </div>
                  <div className="p-4">
                    <div className="font-bold text-slate-800 truncate">{l.title}</div>
                    <div className="flex items-center gap-2 mt-2">
                      <span className="rounded-full bg-amber-100 text-amber-700 px-2 py-0.5 text-xs font-semibold">{l.category}</span>
                      <span className="rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-xs font-semibold">{l.condition}</span>
                    </div>
                    <div className="flex items-center gap-1 text-xs text-slate-400 mt-2">
                      <BadgeCheck className="h-3.5 w-3.5 text-[#2AB7CA]" /> {l.seller_name}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
            {!filtered.length && <p className="text-center text-slate-400 py-20">No items match your filters.</p>}
          </div>
        </div>
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="rounded-3xl max-w-lg" data-testid="listing-detail-dialog">
          {selected && (
            <div>
              <VisuallyHidden><DialogTitle>{selected.title}</DialogTitle></VisuallyHidden>
              <div className="relative rounded-2xl overflow-hidden aspect-[4/3] mb-4">
                <img src={selected.image} alt={selected.title} className="h-full w-full object-cover" />
              </div>
              <div className="flex items-center gap-2 mb-2">
                <span className="rounded-full bg-amber-100 text-amber-700 px-3 py-1 text-xs font-bold">{selected.category}</span>
                <span className="rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-xs font-bold">{selected.condition}</span>
              </div>
              <h3 className="text-2xl font-display font-bold text-slate-900">{selected.title}</h3>
              <p className="text-slate-500 mt-2">{selected.description}</p>
              <div className="flex items-center gap-1 text-sm text-slate-500 mt-3">
                <BadgeCheck className="h-4 w-4 text-[#2AB7CA]" /> Sold by {selected.seller_name}
              </div>
              {selected.seller_user_id !== user?.user_id && (
                <>
                  <Button data-testid="message-seller-btn" variant="outline" onClick={() => { setChatListingId(selected.listing_id); setChatOpen(true); }}
                    className="mt-4 w-full rounded-full font-bold h-11 border-2">
                    <MessageCircle className="h-4 w-4 mr-2" /> Message seller
                  </Button>
                  <button data-testid="report-listing-btn" onClick={() => setReportTarget({ type: "listing", id: selected.listing_id })}
                    className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-400 hover:text-rose-500 transition-colors">
                    <Flag className="h-3.5 w-3.5" /> Report this listing
                  </button>
                </>
              )}
              <div className="flex items-center justify-between mt-6">
                <div className="flex items-center gap-1 text-3xl font-display font-bold text-[#FF5A79]">
                  <Tag className="h-6 w-6" /> ${selected.price.toFixed(2)}
                </div>
                <Button disabled={selected.sold || buying} data-testid="buy-now-btn" onClick={() => buy(selected)}
                  className="rounded-full bg-[#FF5A79] hover:bg-[#e04865] font-bold px-6 h-12 shadow-lg active:scale-95">
                  <ShoppingCart className="h-5 w-5 mr-2" />
                  {selected.sold ? "Sold" : buying ? "Redirecting…" : "Buy Now"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ChatDialog open={chatOpen} onClose={() => { setChatOpen(false); setChatListingId(null); loadUnread(); }} initialListingId={chatListingId} />
      <ReportDialog target={reportTarget} onClose={() => setReportTarget(null)} />
    </div>
  );
}
